import { useCallback, useMemo, useState } from 'react';
import type { LaunchStatus, TestCreativesViewState, UploadedFilter } from '../types/hypothesis';
import { DEFAULT_TEST_CREATIVES_VIEW_STATE } from '../types/hypothesis';
import useOptionalFormContext from './useOptionalFormContext';

type UnpackedOrderUpdater =
  | Record<string, string[]>
  | ((prev: Record<string, string[]>) => Record<string, string[]>);

interface CreativeFiltersShape {
  hypothesis: string[];
  customer: string | null;
  flow: string[];
  technology: string[];
  tpcFinishDate: string | null;
  status: LaunchStatus | null;
  search: string;
}

interface UseTestCreativesFormStateReturn {
  filters: CreativeFiltersShape;
  uploadedFilter: UploadedFilter;
  groupByHypothesis: boolean;
  unpackedOrderByHypothesis: Record<string, string[]>;
  setHypothesis: (val: string[]) => void;
  setCustomer: (val: string | null) => void;
  setFlow: (val: string[]) => void;
  setTechnology: (val: string[]) => void;
  setTpcFinishDate: (val: string | null) => void;
  setStatus: (val: LaunchStatus | null) => void;
  setSearch: (val: string) => void;
  setUploadedFilter: (val: UploadedFilter) => void;
  setGroupByHypothesis: (val: boolean) => void;
  setUnpackedOrderByHypothesis: (updater: UnpackedOrderUpdater) => void;
  resetFilters: () => void;
}

const useTestCreativesFormState = (): UseTestCreativesFormStateReturn => {
  const formContext = useOptionalFormContext();
  const [localState, setLocalState] = useState<TestCreativesViewState>(
    DEFAULT_TEST_CREATIVES_VIEW_STATE,
  );

  const viewState = formContext
    ? (formContext.watch('testCreativesViewState') as TestCreativesViewState)
    : localState;

  const patchViewState = useCallback(
    (patch: Partial<TestCreativesViewState>) => {
      if (formContext) {
        const current = formContext.getValues(
          'testCreativesViewState',
        ) as TestCreativesViewState;
        formContext.setValue('testCreativesViewState', {
          ...current,
          ...patch,
        });
        return;
      }

      setLocalState((prev) => ({ ...prev, ...patch }));
    },
    [formContext],
  );

  const setHypothesis = useCallback(
    (val: string[]) => { patchViewState({ hypothesis: val }); },
    [patchViewState],
  );
  const setCustomer = useCallback(
    (val: string | null) => { patchViewState({ customer: val }); },
    [patchViewState],
  );
  const setFlow = useCallback(
    (val: string[]) => { patchViewState({ flow: val }); },
    [patchViewState],
  );
  const setTechnology = useCallback(
    (val: string[]) => { patchViewState({ technology: val }); },
    [patchViewState],
  );
  const setTpcFinishDate = useCallback(
    (val: string | null) => { patchViewState({ tpcFinishDate: val }); },
    [patchViewState],
  );
  const setStatus = useCallback(
    (val: LaunchStatus | null) => { patchViewState({ status: val }); },
    [patchViewState],
  );
  const setSearch = useCallback(
    (val: string) => { patchViewState({ search: val }); },
    [patchViewState],
  );
  const setUploadedFilter = useCallback(
    (val: UploadedFilter) => { patchViewState({ uploaded: val }); },
    [patchViewState],
  );
  const setGroupByHypothesis = useCallback(
    (val: boolean) => { patchViewState({ groupByHypothesis: val }); },
    [patchViewState],
  );

  const setUnpackedOrderByHypothesis = useCallback(
    (updater: UnpackedOrderUpdater) => {
      if (formContext) {
        const current = (
          formContext.getValues('testCreativesViewState') as TestCreativesViewState
        ).unpackedOrderByHypothesis;
        const next =
          typeof updater === 'function' ? updater(current) : updater;
        patchViewState({ unpackedOrderByHypothesis: next });
        return;
      }

      setLocalState((prev) => ({
        ...prev,
        unpackedOrderByHypothesis:
          typeof updater === 'function'
            ? updater(prev.unpackedOrderByHypothesis)
            : updater,
      }));
    },
    [formContext, patchViewState],
  );

  const resetFilters = useCallback(() => {
    patchViewState({
      search: '',
      hypothesis: [],
      customer: null,
      flow: [],
      technology: [],
      tpcFinishDate: null,
      status: null,
      uploaded: 'last_week',
    });
  }, [patchViewState]);

  const filters = useMemo<CreativeFiltersShape>(
    () => ({
      hypothesis: viewState.hypothesis,
      customer: viewState.customer,
      flow: viewState.flow,
      technology: viewState.technology,
      tpcFinishDate: viewState.tpcFinishDate,
      status: viewState.status,
      search: viewState.search,
    }),
    [viewState],
  );

  return {
    filters,
    uploadedFilter: viewState.uploaded,
    groupByHypothesis: viewState.groupByHypothesis,
    unpackedOrderByHypothesis: viewState.unpackedOrderByHypothesis,
    setHypothesis,
    setCustomer,
    setFlow,
    setTechnology,
    setTpcFinishDate,
    setStatus,
    setSearch,
    setUploadedFilter,
    setGroupByHypothesis,
    setUnpackedOrderByHypothesis,
    resetFilters,
  };
};

export default useTestCreativesFormState;
